import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useClients } from "@/context/ClientContext";
import api from "@/lib/api";
import { LanguageSwitch, useLanguage } from "@/context/LanguageContext";
import {
  LayoutDashboard, Upload, CalendarDays, FolderOpen, BarChart3, Sparkles,
  MessageSquare, Plug, Building2, Users, ScrollText, Shield, LogOut, ChevronDown, Bell, ShieldAlert,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { group: "Struktur" },
  { to: "/mandanten", label: "Mandanten & Projekte", icon: Building2, testid: "sidebar-nav-mandanten", roles: null },
  { to: "/accounts", label: "Social Accounts", icon: Plug, testid: "sidebar-nav-accounts", roles: null, reviewAllowed: true },
  { to: "/", label: "Übersicht", icon: LayoutDashboard, testid: "sidebar-nav-dashboard", roles: null },
  { group: "Arbeiten" },
  { to: "/import", label: "Bulk-Import", icon: Upload, testid: "sidebar-nav-import", roles: ["admin", "editor"], badge: "Kern" },
  { to: "/plan", label: "Redaktionsplan", icon: CalendarDays, testid: "sidebar-nav-plan", roles: null, reviewAllowed: true },
  { to: "/media", label: "Medienbibliothek", icon: FolderOpen, testid: "sidebar-nav-media", roles: null },
  { group: "Auswerten" },
  { to: "/analyse", label: "Analyse", icon: BarChart3, testid: "sidebar-nav-analyse", roles: null },
  { to: "/performance", label: "Optimierung", icon: Sparkles, testid: "sidebar-nav-performance", roles: null },
  { to: "/kommentare", label: "Inbox / Kommentare", icon: MessageSquare, testid: "sidebar-nav-kommentare", roles: null },
  { group: "Kontrolle" },
  { to: "/fristen", label: "Fristen & Compliance", icon: ShieldAlert, testid: "sidebar-nav-fristen", roles: null },
  { group: "System" },
  { to: "/regeln", label: "Plattform-Regeln", icon: Shield, testid: "sidebar-nav-regeln", roles: null },
  { to: "/nutzer", label: "Nutzer & Rollen", icon: Users, testid: "sidebar-nav-nutzer", roles: ["admin"] },
  { to: "/audit", label: "Audit-Log", icon: ScrollText, testid: "sidebar-nav-audit", roles: ["admin", "analyst", "reviewer"] },
];

export default function Layout({ children }) {
  const { t, locale } = useLanguage();
  const { user, logout, can } = useAuth();
  const { clients, current, select } = useClients();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [attention, setAttention] = useState(0);
  const tenantPages = ["/", "/mandanten", "/media", "/analyse", "/performance", "/kommentare", "/fristen", "/regeln", "/audit"];
  const visibleToReviewer = (item) => !user?.review_access || item.reviewAllowed || (user.review_access.tenant_view && tenantPages.includes(item.to));

  useEffect(() => {
    if (user?.review_access) return;
    api.get("/notifications").then(({ data }) => setNotifs(data)).catch(() => {});
    api.get("/compliance/summary").then(({ data }) => setAttention(data.attention || 0)).catch(() => {});
  }, [user]);
  const unread = notifs.filter((n) => !n.read).length;

  return (
    <div className="min-h-screen flex bg-[#F8F9FA]">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 bg-white border-r border-slate-200 flex flex-col fixed h-screen">
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-slate-200">
          <div className="h-8 w-8 rounded-md bg-white flex items-center justify-center overflow-hidden">
            <img src="/comrocket-social-icon.jpg" alt="Comrocket" className="h-8 w-8 object-cover" />
          </div>
          <div className="leading-tight">
            <div className="font-display font-extrabold text-[15px]">Social Control</div>
            <div className="overline">Center</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
          {NAV.map((n, idx) => {
            if (n.group) {
              // hide group header if all following items until next group are role-hidden
              const rest = NAV.slice(idx + 1);
              const until = [];
              for (const r of rest) { if (r.group) break; until.push(r); }
              const anyVisible = until.some((r) =>
                visibleToReviewer(r) && (!r.roles || can(...r.roles))
              );
              if (!anyVisible) return null;
              return <div key={`g-${n.group}`} className="overline px-3 pt-4 pb-1.5 first:pt-1">{t(n.group)}</div>;
            }
            if (!visibleToReviewer(n)) return null;
            if (n.roles && !can(...n.roles)) return null;
            return (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={n.testid}
                className={({ isActive }) =>
                  `group flex items-center gap-3 px-3 py-2 rounded-md text-[13.5px] font-medium transition-colors duration-150 ${
                    isActive ? "bg-[#0A0A0A] text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`}>
                <n.icon size={17} strokeWidth={2} />
                <span className="flex-1">{t(n.label)}</span>
                {n.to === "/fristen" && attention > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-rose-500 text-white">{attention}</span>}
                {n.badge && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-700">{t(n.badge)}</span>}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-200 text-[11px] text-slate-400 font-mono">v0.1 · MVP</div>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-64 flex flex-col min-h-screen">
        <header className="h-16 sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-slate-200 flex items-center justify-between px-6">
          {/* Client selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="client-selector" className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 transition-colors duration-150">
                <Building2 size={15} className="text-slate-500" />
                <span className="font-semibold text-sm">{current?.name || t("Mandant wählen")}</span>
                <ChevronDown size={14} className="text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
              <DropdownMenuLabel className="overline">{t("Mandant wechseln")}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {clients.map((c) => (
                <DropdownMenuItem key={c.id} data-testid={`client-option-${c.id}`} onClick={() => select(c)} className="cursor-pointer">
                  <div className="flex-1">
                    <div className="font-medium text-sm">{c.name}</div>
                    <div className="text-xs text-slate-400">{c.project_count} {t("Projekte")} · {c.asset_count} {t("Assets")}</div>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="flex items-center gap-3">
            <LanguageSwitch />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="notifications-btn" className="relative p-2 rounded-md hover:bg-slate-100 transition-colors duration-150">
                  <Bell size={18} className="text-slate-600" />
                  {unread > 0 && <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-rose-500" />}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel className="overline">{t("Benachrichtigungen")}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {notifs.length === 0 && <div className="px-3 py-4 text-sm text-slate-400">{t("Keine Benachrichtigungen")}</div>}
                {notifs.slice(0, 8).map((n) => (
                  <div key={n.id} className="px-3 py-2 text-sm border-b border-slate-100 last:border-0">
                    <div className="text-slate-700">{n.message}</div>
                    <div className="text-[11px] text-slate-400 mt-0.5">{new Date(n.created_at).toLocaleString(locale)}</div>
                  </div>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-100 transition-colors duration-150">
                  <div className="h-7 w-7 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center text-xs font-semibold">
                    {(user?.name || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="text-left leading-tight hidden sm:block">
                    <div className="text-[13px] font-semibold">{user?.name}</div>
                    <div className="text-[11px] text-slate-400 capitalize">{user?.role}</div>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="cursor-pointer text-rose-600">
                  <LogOut size={15} className="mr-2" /> {t("Abmelden")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-6">
          {user?.review_access?.tenant_view && <div role="status" className="mb-4 rounded-md border border-sky-200 bg-sky-50 px-4 py-2 text-sm text-sky-800">
            {user.review_access.meta_actions && !user.review_access.read_only
              ? (locale === "en" ? "Review access: Facebook/Instagram connection, publishing and synchronization are enabled for this tenant only." : "Review-Zugang: Facebook/Instagram verbinden, veröffentlichen und synchronisieren ist nur für diesen Mandanten freigegeben.")
              : (locale === "en" ? "Review access: read-only access to this tenant. Changes and publishing are disabled." : "Review-Zugang: Lesezugriff auf diesen Mandanten. Änderungen und Veröffentlichungen sind gesperrt.")}
          </div>}
          {children}
        </main>
      </div>
    </div>
  );
}
